import React from 'react'
import { StarFilled, StarOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { IsoBox } from '../menus/IsoBox'
import type { DisplayOptionDto } from '../services/display-option.service'
import { deriveOrientationDims } from '../model/display-options'
import type { OrientationKey } from '../model/display-options'

/**
 * feat-004 story 2.4 (AC1) — the master list: 6 fixed-order orientation rows
 * + a Display Modes section listing EVERY mode row, including
 * disabled/archived (this is the MANAGEMENT view — the opposite filter from
 * story 2.2's picker projection, which excludes those).
 */
export interface OptionListProps {
  options: DisplayOptionDto[]
  selectedUuid: string | null
  onSelect: (uuid: string) => void
  baseImageUrl: string
  readOnly: boolean
  /** Opens the "Add display mode" creation dialog. Omitted (or readOnly)
   *  hides the trigger — creation is gated by DisplayOptionCapability.Create
   *  at the DisplayOptionsCard level, same pattern as canUploadImage. */
  onAddMode?: () => void
  /** The product's base W/H/D, so each row's isometric box can be drawn at
   *  that option's own proportions (PLAN-TC-1376/1377). An orientation row
   *  stores no dims of its own — they are permuted from these. Optional,
   *  matching every other back-compat prop on this card: without it the
   *  boxes stay the plain cube they were. */
  baseDims?: { width: number; height: number; depth: number }
}

const ORIENTATION_LABEL_KEYS: Record<number, string> = {
  1: 'simulationV3.displayOptions.orientation.front',
  2: 'simulationV3.displayOptions.orientation.frontLaydown',
  3: 'simulationV3.displayOptions.orientation.side',
  4: 'simulationV3.displayOptions.orientation.sideLaydown',
  5: 'simulationV3.displayOptions.orientation.top',
  6: 'simulationV3.displayOptions.orientation.topRotated',
}

const ORIENTATION_DEFAULT_LABELS: Record<number, string> = {
  1: 'Front',
  2: 'Front Laydown',
  3: 'Side',
  4: 'Side Laydown',
  5: 'Top',
  6: 'Top Rotated',
}

/** "‹code› – ‹translated label›" — UX spec's locked orientation-naming
 *  pattern, with ONE deviation: the separator is an EN dash (U+2013), not
 *  the em dash the spec writes (reported 2026-10-13, "make these dashes
 *  smaller — these are long"). Separator only; the structure is untouched.
 *  `export/shared/orientation-column.ts` deliberately duplicates this
 *  function for the report cells (it stays i18n-import-free) — change the
 *  separator in BOTH or the screen and its own export disagree. */
export function orientationDisplayName(
  t: (key: string, opts?: Record<string, unknown>) => string,
  orientationKey: number
): string {
  const label = t(ORIENTATION_LABEL_KEYS[orientationKey], {
    defaultValue: ORIENTATION_DEFAULT_LABELS[orientationKey],
  })
  return `${orientationKey} – ${label}`
}

/** The word shown on (and as the star's tooltip for) the default option.
 *  One function so the list row, the star tooltip and the detail pane's
 *  title can never word the same marker differently. */
export function defaultOptionLabel(
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  return t('simulationV3.displayOptions.list.default', {
    defaultValue: 'Default',
  })
}

export const OptionList: React.FC<OptionListProps> = ({
  options,
  selectedUuid,
  onSelect,
  // `baseImageUrl` stays on the props contract (hosts still pass it) but is
  // no longer bound here — the per-row image preview it fed was withdrawn
  // with the dedicated-image UI (see OptionDetail's 2026-09-10 note).
  readOnly,
  onAddMode,
  baseDims,
}) => {
  const { t } = useTranslation()

  /** One string for both the row's "Default" word and the star's tooltip,
   *  so the two can never drift apart. */
  const defaultLabel = defaultOptionLabel(t)

  /** An option's effective W/H/D for the row's isometric box — permuted
   *  from the product's base dims for an orientation, the mode's own saved
   *  dims for a display mode. Null whenever neither is available. */
  const isoDimsOf = (option: DisplayOptionDto) => {
    if (option.type === 'orientation') {
      if (
        !baseDims ||
        option.orientationKey == null ||
        option.orientationKey < 1 ||
        option.orientationKey > 6
      ) {
        return null
      }
      return deriveOrientationDims(
        baseDims,
        option.orientationKey as OrientationKey
      )
    }
    return option.width != null &&
      option.height != null &&
      option.depth != null
      ? { width: option.width, height: option.height, depth: option.depth }
      : null
  }

  const orientations = options
    .filter((o) => o.type === 'orientation')
    .sort((a, b) => (a.orientationKey ?? 0) - (b.orientationKey ?? 0))
  // Display modes keep the backend's own order (creation order, oldest
  // first — the DAO's findAllByProductId orders by id ASC). A newly created
  // mode must always land at the bottom, never reshuffled by a re-sort here;
  // sorting by uuid/name was tried before and is explicitly wrong (uuid is
  // random, name ordering is explicitly not what's wanted either).
  const modes = options.filter((o) => o.type === 'display_mode')

  const renderRow = (option: DisplayOptionDto) => {
    const label =
      option.type === 'orientation' && option.orientationKey != null
        ? orientationDisplayName(t, option.orientationKey)
        : option.name || ''
    // UX spec (feat-004) §A11y: "disabled = grey + lock + reason text" —
    // the lock icon's tooltip already carries the reason, but a tooltip
    // alone is hover-only. Reported by QA (2026-10-21): the SOW requires
    // the disabled state to be visible on the row itself, not just on
    // hover — grey text plus an inline "(disabled)" marker next to the
    // name, for every disabled row (orientation or display mode alike).
    const isDisabled = !option.isEnabled
    /* The padlock badge that used to close every row was withdrawn
       (user, 2026-10-29: "the lock icon needs to be removed, it's not
       necessary — only the lock icon"). It carried no action of its own:
       selection is never blocked here, and the one piece of information it
       held that the row does not already show — WHY a row is locked — lived
       in a hover-only tooltip. What the SOW's A11y line actually demands of
       a disabled row, the grey text plus the inline "(disabled)" marker
       above, is untouched; what blocks a real action still says so on the
       button in the detail pane, which is where the action is. The
       `list.orientationLocked*` / `list.archived` / `list.disabled` strings
       stay in the translation files — nothing else reads them today, but
       they are the wording to reuse if the reason is ever surfaced again. */

    return (
      <div
        key={option.uuid}
        className={`simv3-doc__row${selectedUuid === option.uuid ? ' simv3-doc__row--selected' : ''}`}
        onClick={() => onSelect(option.uuid)}
        role="option"
        aria-selected={selectedUuid === option.uuid}
        data-testid={`option-row-${option.uuid}`}
      >
        {/* Reported 2026-09-10: keep the isometric box outline as a
            per-row visual, but drop the base-product photo it always
            showed here (never a real per-option image anyway — see
            deferred-work.md's "OptionList's rows never show the
            dedicated image" note). */}
        {/* Same proportioning as the detail pane's hero box, so a row and
            the detail it opens never disagree about an option's shape. */}
        <IsoBox
          imageUrl=""
          orientationKey={option.orientationKey}
          isDedicated={false}
          size="S"
          label={label}
          dims={isoDimsOf(option)}
        />
        <span
          className={`simv3-doc__row-label${isDisabled ? ' simv3-doc__row-label--disabled' : ''}`}
        >
          {label}
          {isDisabled ? (
            <span className="simv3-doc__row-disabled-tag">
              {' '}
              (
              {t('simulationV3.displayOptions.list.disabledTag', {
                defaultValue: 'disabled',
              })}
              )
            </span>
          ) : null}
        </span>
        {/* Pack capacity (units per pack, PRD FR-2) beside a display mode's
            name — the client wireframe's "cap. 16". Orientations are always
            1 unit, so they carry no marker. Same metadata voice as the
            "Default" word, so the icon column stays aligned. */}
        {option.type === 'display_mode' && option.capacity != null ? (
          <span
            className="simv3-doc__row-capacity"
            data-testid={`option-row-capacity-${option.uuid}`}
          >
            {t('simulationV3.displayOptions.list.capacity', {
              defaultValue: 'cap. {{units}}',
              units: option.capacity,
            })}
          </span>
        ) : null}
        {option.isDefault ? (
          <>
            {/* Reported 2026-10-13: the filled star alone is easy to miss
                on a dense list — say "Default" in words too, in the
                italic 12px/400 metadata voice the row annotations use. */}
            <span className="simv3-doc__row-default">{defaultLabel}</span>
            <StarFilled title={defaultLabel} />
          </>
        ) : (
          <StarOutlined style={{ opacity: 0.3 }} />
        )}
      </div>
    )
  }

  return (
    <div
      className={`simv3-doc__list${readOnly ? ' simv3-doc--readonly' : ''}`}
      role="listbox"
      data-testid="option-list"
    >
      <div className="simv3-doc__section-title">
        {t('simulationV3.displayOptions.list.orientations', {
          defaultValue: 'Orientations',
        })}{' '}
        {/* Client wireframe: say how each section's rows come to exist —
            orientations are derived automatically from the base dims,
            display modes are added by hand. */}
        <span
          className="simv3-doc__section-hint"
          data-testid="option-list-orientations-hint"
        >
          (
          {t('simulationV3.displayOptions.list.orientationsHint', {
            defaultValue: 'automatic',
          })}
          )
        </span>
      </div>
      {orientations.map(renderRow)}
      <div className="simv3-doc__section-title">
        {t('simulationV3.displayOptions.list.displayModes', {
          defaultValue: 'Display Modes',
        })}{' '}
        <span
          className="simv3-doc__section-hint"
          data-testid="option-list-display-modes-hint"
        >
          (
          {t('simulationV3.displayOptions.list.displayModesHint', {
            defaultValue: 'manual',
          })}
          )
        </span>
      </div>
      {modes.length === 0 ? (
        <span className="simv3-doc__lock-reason">
          {t('simulationV3.displayOptions.list.noModes', {
            defaultValue: 'No display modes yet',
          })}
        </span>
      ) : (
        modes.map(renderRow)
      )}
      {!readOnly && onAddMode ? (
        <button
          type="button"
          className="simv3-doc__add-mode-btn"
          onClick={onAddMode}
          data-testid="add-display-mode-button"
        >
          {t('simulationV3.displayOptions.list.addMode', {
            defaultValue: '+ Add display mode',
          })}
        </button>
      ) : null}
    </div>
  )
}
